import React from "react";
import moment from "moment";
import { chain } from "lodash";
import { ROLES } from "src/constants";
import MapitShipOrderStatus from "./MapitShipOrderStatus";
import {
  WarehouseCell,
  CustomerNameCell,
  CustomerAddressCell,
  ShippingCompanyCell,
  ShipmentTrackingCell,
  ReturnShipmentCell,
} from "./TableCells";
import { UserAccountNavigate } from "src/_modules/common";

const $COLUMN_WIDTH = 200;

/**
 * Formats date for display
 */
const formatDate = (params) => moment(params.value).format("DD/MM/YY - h:mm A");

/**
 * Gets date from status track by status name
 */
const getDateFromStatusTrack = (statusTrack, statusName, position = "last") => {
  const filterFn = (el) => el.status === statusName;
  const date = chain(statusTrack)
    .filter(filterFn)
    [position]()
    .get("dateTime")
    .value();
  return date ? moment(date).format("DD/MM/YY - h:mm A") : "---";
};

/**
 * Creates base columns that are always shown
 */
const createBaseColumns = (t, isAdmin) => {
  const cols = [
    {
      field: "orderNumber",
      sortable: false,
      headerName: t("Order No."),
      width: $COLUMN_WIDTH * 0.5,
    },
    {
      field: "merchantOrderNumber",
      sortable: false,
      headerName: t("Refrence Number"),
      width: $COLUMN_WIDTH * 0.6,
    },
    {
      field: "merchant",
      sortable: false,
      renderCell: ({ value }) => <UserAccountNavigate user={value} />,
      headerName: t("Merchant"),
      width: $COLUMN_WIDTH * 1,
    },
    {
      field: "warehouse",
      sortable: false,
      renderCell: (params) => <WarehouseCell value={params.value} />,
      headerName: t("Pickup Location"),
      width: $COLUMN_WIDTH * 1,
    },
    {
      field: "customerName",
      sortable: false,
      renderCell: (params) => <CustomerNameCell row={params.row} />,
      headerName: t("Customer Name"),
      width: $COLUMN_WIDTH * 0.8,
    },
    {
      field: "customerAddress",
      sortable: false,
      renderCell: (params) => <CustomerAddressCell row={params.row} />,
      headerName: t("Customer Address"),
      width: $COLUMN_WIDTH * 1,
    },
    {
      field: "currentStatus",
      sortable: false,
      renderCell: (params) => (
        <MapitShipOrderStatus row={params.row} value={params.value} />
      ),
      headerName: t("Status"),
      width: $COLUMN_WIDTH * 0.9,
    },
    {
      field: "amountPayable",
      sortable: false,
      headerName: t("COD (Cash On Delivery)"),
      width: $COLUMN_WIDTH * 0.7,
    },
    {
      field: "shippingConst",
      sortable: false,
      valueGetter: ({ row }) => {
        const shipment =
          row?.otoIntegration?.returnShipment ?? row?.otoIntegration?.shipment;
        return shipment?.deliveryOption?.price;
      },
      headerName: t("Shipping cost"),
      width: $COLUMN_WIDTH * 0.7,
    },
    {
      field: "otoIntegration",
      sortable: false,
      renderCell: (params) => <ShippingCompanyCell value={params.value} />,
      headerName: t("Shipping Company"),
      width: $COLUMN_WIDTH * 1,
    },
  ];

  // Remove merchant column for non-admin users
  if (!isAdmin) {
    return cols.filter((col) => col.field !== "merchant");
  }

  return cols;
};

/**
 * Creates tracking number column
 */
const createTrackingColumn = (t) => ({
  field: "trackingNumber",
  headerName: t("Shipment Number"),
  width: $COLUMN_WIDTH * 0.8,
  sortable: false,
  renderCell: (params) => <ShipmentTrackingCell row={params.row} />,
});

/**
 * Creates return shipment column
 */
const createReturnColumn = (t, onCreateReturn) => ({
  field: "returned",
  sortable: false,
  width: $COLUMN_WIDTH * 0.8,
  headerName: t("Return Shipment"),
  renderCell: (params) => (
    <ReturnShipmentCell row={params.row} onCreateReturn={onCreateReturn} />
  ),
});

/**
 * Creates date columns based on current status tab
 */
const createDateColumns = (t, status) => {
  const dateColumns = [];

  switch (status) {
    case "ORDER_PENDING":
      dateColumns.push({
        field: "createdAt",
        sortable: false,
        valueGetter: formatDate,
        headerName: t("Order Creation Date"),
        width: $COLUMN_WIDTH * 1,
      });
      break;

    case "ORDER_ASSIGNED_TO_SHIPPING_COMPANY":
      dateColumns.push(
        {
          field: "createdAt",
          sortable: false,
          valueGetter: formatDate,
          headerName: t("Order Creation Date"),
          width: $COLUMN_WIDTH * 0.8,
        },
        {
          field: "shipmentCreatedAt",
          sortable: false,
          valueGetter: ({ row }) =>
            getDateFromStatusTrack(
              row?.statusTrack,
              "ORDER_ASSIGNED_TO_SHIPPING_COMPANY",
            ),
          headerName: t("Shipment Creation Date"),
          width: $COLUMN_WIDTH * 0.8,
        },
      );
      break;

    case "ORDER_STARTED":
      dateColumns.push(
        {
          field: "shipmentCreatedAt",
          sortable: false,
          checkboxSelection: true,
          valueGetter: ({ row }) =>
            getDateFromStatusTrack(
              row?.statusTrack,
              "ORDER_ASSIGNED_TO_SHIPPING_COMPANY",
            ),
          headerName: t("Shipment Creation Date"),
          width: $COLUMN_WIDTH * 0.8,
        },
        {
          field: "pickupDate",
          sortable: false,
          valueGetter: ({ row }) =>
            getDateFromStatusTrack(row?.statusTrack, "ORDER_STARTED", "first"),
          headerName: t("Pickup Date"),
          width: $COLUMN_WIDTH * 0.8,
        },
      );
      break;

    case "ORDER_ON_HOLD":
      dateColumns.push(
        {
          field: "pickupDate",
          sortable: false,
          valueGetter: ({ row }) =>
            getDateFromStatusTrack(row?.statusTrack, "ORDER_STARTED"),
          headerName: t("Pickup Date"),
          width: $COLUMN_WIDTH * 0.8,
        },
        {
          field: "shipmentCreatedAt",
          sortable: false,
          valueGetter: ({ row }) =>
            getDateFromStatusTrack(
              row?.statusTrack,
              "ORDER_ASSIGNED_TO_SHIPPING_COMPANY",
            ),
          headerName: t("Shipment Creation Date"),
          width: $COLUMN_WIDTH * 0.8,
        },
      );
      break;

    case "ORDER_COMPLETED":
      dateColumns.push(
        {
          field: "shipmentCreatedAt",
          sortable: false,
          valueGetter: ({ row }) =>
            getDateFromStatusTrack(
              row?.statusTrack,
              "ORDER_ASSIGNED_TO_SHIPPING_COMPANY",
            ),
          headerName: t("Shipment Creation Date"),
          width: $COLUMN_WIDTH * 0.8,
        },
        {
          field: "deliveryDate",
          sortable: false,
          valueGetter: ({ row }) =>
            getDateFromStatusTrack(row?.statusTrack, "ORDER_COMPLETED"),
          headerName: t("statusDate.ORDER_COMPLETED"),
          width: $COLUMN_WIDTH * 0.8,
        },
      );
      break;

    case "ORDER_RETURN":
      dateColumns.push(
        {
          field: "shipmentCreatedAt",
          sortable: false,
          valueGetter: ({ row }) =>
            getDateFromStatusTrack(
              row?.statusTrack,
              "ORDER_ASSIGNED_TO_SHIPPING_COMPANY",
            ),
          headerName: t("Shipment Creation Date"),
          width: $COLUMN_WIDTH * 0.8,
        },
        {
          field: "returnDate",
          sortable: false,
          valueGetter: ({ row }) =>
            getDateFromStatusTrack(row?.statusTrack, "ORDER_RETURN"),
          headerName: t("statusDate.ORDER_RETURN"),
          width: $COLUMN_WIDTH * 0.8,
        },
      );
      break;

    default:
      // ALL_ORDERS and other statuses
      if (!["ALL_ORDERS", "ORDER_PENDING"].includes(status) && status) {
        dateColumns.push({
          field: status,
          sortable: false,
          valueGetter: ({ row }) =>
            getDateFromStatusTrack(row?.statusTrack, status),
          headerName: t(`statusDate.${status}`),
          width: $COLUMN_WIDTH * 0.8,
        });
      }
      break;
  }

  return dateColumns;
};

/**
 * Generates columns configuration based on status and user role
 *
 * @param {Object} options - Configuration options
 * @param {Function} options.t - Translation function
 * @param {string} options.status - Current status tab
 * @param {Object} options.profile - User profile
 * @param {Function} options.onCreateReturn - Callback for creating return shipment
 * @returns {Array} Column definitions
 */
export const getColumns = ({ t, status, profile, onCreateReturn }) => {
  const isAdmin = profile?.role === ROLES.admin;
  const isMerchant = profile?.merchant?.role === ROLES.merchant;

  // Start with base columns
  let cols = createBaseColumns(t, isAdmin);

  // Remove shipping columns for pending/canceled orders
  if (["ORDER_PENDING", "ORDER_CANCELED"].includes(status)) {
    cols = cols.filter(
      (col) => !["shippingConst", "otoIntegration"].includes(col.field),
    );
  }

  // Add tracking number column (after status column, index 5)
  if (!["ORDER_CANCELED", "ORDER_PENDING"].includes(status)) {
    cols.splice(5, 0, createTrackingColumn(t));
  }

  // Add return shipment column for completed orders (merchant only)
  if (status === "ORDER_COMPLETED") {
    cols.push(createReturnColumn(t, onCreateReturn));
  }

  // Add date columns based on status
  cols.push(...createDateColumns(t, status));

  return cols;
};

export default getColumns;
